import React from 'react';
import { Row, Col, Card, Statistic, Progress, Table, Typography } from 'antd';
import { ProjectOutlined } from '@ant-design/icons';
import type { ProjectStatus, TopCustomerRow } from '@/services/dashboard.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import { EnterpriseTokens } from '@/theme/enterprise-tokens';

const { Text } = Typography;

export interface ExecutiveProjectAndCustomersProps {
  projectStatus: ProjectStatus[];
  topCustomers: TopCustomerRow[];
  loading: boolean;
}

export const ExecutiveProjectAndCustomers: React.FC<ExecutiveProjectAndCustomersProps> = ({
  projectStatus,
  topCustomers,
  loading,
}) => {
  const { formatCurrency } = useCurrency();
  const maxProject = Math.max(1, ...projectStatus.map((p) => p.count));

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: EnterpriseTokens.spacing.xl }}>
      <Col span={24}>
        <Card
          title="Project pipeline"
          bordered={false}
          style={{ border: EnterpriseTokens.workspace.borderSubtle }}
          loading={loading}
        >
          {projectStatus.length === 0 ? (
            <Text type="secondary">No project status breakdown available.</Text>
          ) : (
            <Row gutter={[16, 16]}>
              {projectStatus.map((item, index) => (
                <Col xs={24} sm={12} lg={6} key={index}>
                  <Card size="small" style={{ background: EnterpriseTokens.workspace.surfaceMuted }}>
                    <Statistic
                      title={item.status}
                      value={item.count}
                      valueStyle={{ color: item.color }}
                      prefix={<ProjectOutlined />}
                    />
                    <Progress
                      percent={Math.round((item.count / maxProject) * 100)}
                      strokeColor={item.color}
                      showInfo={false}
                      style={{ marginTop: 8 }}
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Card>
      </Col>
      <Col span={24}>
        <Card
          title="Top customers by invoiced value"
          bordered={false}
          style={{ border: EnterpriseTokens.workspace.borderSubtle }}
          loading={loading}
        >
          {topCustomers.length === 0 ? (
            <Text type="secondary">No customer ranking data for this period.</Text>
          ) : (
            <Table
              size="small"
              pagination={false}
              rowKey={(r, i) => `${r.name}-${i}`}
              dataSource={topCustomers}
              columns={[
                { title: 'Customer', dataIndex: 'name', key: 'name' },
                {
                  title: 'Invoiced',
                  dataIndex: 'value',
                  key: 'value',
                  align: 'right' as const,
                  render: (v: number, row: TopCustomerRow) =>
                    row.valueFormatted ?? formatCurrency(v ?? 0),
                },
                { title: 'Projects', dataIndex: 'projects', key: 'projects', width: 100 },
                {
                  title: 'Health',
                  dataIndex: 'status',
                  key: 'status',
                  width: 100,
                  render: (s: string) => s || '—',
                },
              ]}
            />
          )}
        </Card>
      </Col>
    </Row>
  );
};
